<script setup lang="ts">
import type { SiteId } from "@/types/common/ids";

import { useQuery } from "@tanstack/vue-query";
import { useRouteParams, useRouteQuery } from "@vueuse/router";
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { siteDetailsOptions } from "@/app/site/routes/[siteId]/hooks/use-site-details.ts";
import {
  isTransferSignaturePromptRequested,
  TRANSFER_SIGNATURE_PROMPT_QUERY_PARAM,
} from "@/domains/voltacall/transfer-signature-prompt";
import { useBackofficeClient } from "@/hooks/backoffice/use-backoffice-client";
import { branded } from "@/lib/helpers/branded-helper.ts";

import SubscriberTransferSignatureModal from "@/components/subscriber/subscriber-transfer-signature-modal.vue";

const route = useRoute();
const router = useRouter();
const backofficeClient = useBackofficeClient();

const siteIdRouteParam = useRouteParams<SiteId>("siteId");
const transferSignaturePromptQueryParam = useRouteQuery(TRANSFER_SIGNATURE_PROMPT_QUERY_PARAM);

const promptedSiteId = ref<SiteId>();
const isVisible = ref(false);

watch(
  transferSignaturePromptQueryParam,
  (rawFlag) => {
    if (!isTransferSignaturePromptRequested(rawFlag)) {
      return;
    }

    const siteId = branded.siteId.safeParse(Number(siteIdRouteParam.value));
    if (!siteId.success) {
      return;
    }

    promptedSiteId.value = siteId.data;
    isVisible.value = true;

    const remainingQuery = { ...route.query };
    delete remainingQuery[TRANSFER_SIGNATURE_PROMPT_QUERY_PARAM];
    void router.replace({ query: remainingQuery });
  },
  { immediate: true },
);

const isPrompted = computed(() => promptedSiteId.value !== undefined);

const { data: site } = useQuery(siteDetailsOptions(promptedSiteId, isPrompted, backofficeClient));
</script>

<template>
  <SubscriberTransferSignatureModal
    v-if="site"
    v-model:visible="isVisible"
    :site="site"
  />
</template>

<style scoped></style>
